import { lazy } from 'react';

import AuthGuard from 'components/AuthGuard';
import DashboardLayout from 'layouts/DashboardLayout';
import NoteLayout from 'layouts/NoteLayout';

const error404 = {
  component: lazy(() => import('views/Errors/Error404')),
};

const dashboardRoutes = {
  path: '/',
  guard: AuthGuard,
  layout: DashboardLayout,
  routes: [
    {
      component: lazy(() => import('views/Assets/List')),
      exact: true,
      path: '/assets',
      permission: 'assets.select.listAssets',
    },
    {
      component: lazy(() => import('views/Assets/Edit')),
      exact: true,
      path: '/assets/add',
      permission: 'assets.insert.addAssets',
    },
    {
      component: lazy(() => import('views/AssetAssignments')),
      exact: true,
      path: '/assets/assignments',
      permission: 'assetAssignments.select.listAssetAssignments',
    },
    {
      component: lazy(() => import('views/Assets/Edit')),
      exact: true,
      path: '/assets/:id',
      permission: 'assets.update.editAssets',
    },
    {
      component: lazy(() => import('views/Assets/Groups')),
      exact: true,
      path: '/assets/groups/list',
    },
    {
      component: lazy(() => import('views/Banks/Inquiries/AccountBalance')),
      exact: true,
      path: '/banks/inquiries/account_balance',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Inquiries/AccountStatus')),
      exact: true,
      path: '/banks/inquiries/account_status',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Inquiries/Requests')),
      exact: true,
      path: '/banks/inquiries/requests',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Accounts/List')),
      exact: true,
      path: '/banks/normativity/accounts',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Accounts/Edit')),
      exact: true,
      path: '/banks/normativity/accounts/add',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Accounts/Edit')),
      exact: true,
      path: '/banks/normativity/accounts/:accountId',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/AccountsTypes/List')),
      exact: true,
      path: '/banks/normativity/accountstypes',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/AccountsTypes/Edit')),
      exact: true,
      path: '/banks/normativity/accountstypes/add',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/AccountsTypes/Edit')),
      exact: true,
      path: '/banks/normativity/accountstypes/:accountTypeId',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Authorization/Levels/List')),
      exact: true,
      path: '/banks/normativity/authorization/levels',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Authorization/Levels/Edit')),
      exact: true,
      path: '/banks/normativity/authorization/levels/add',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Authorization/Levels/Edit')),
      exact: true,
      path: '/banks/normativity/authorization/levels/:levelId',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Checkbooks/List')),
      exact: true,
      path: '/banks/normativity/checkbooks',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Providers/List')),
      exact: true,
      path: '/banks/normativity/providers',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Providers/Edit')),
      exact: true,
      path: '/banks/normativity/providers/add',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Providers/Edit')),
      exact: true,
      path: '/banks/normativity/providers/:providerId',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Taxes/List')),
      exact: true,
      path: '/banks/normativity/taxes',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Taxes/Edit')),
      exact: true,
      path: '/banks/normativity/taxes/add',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Taxes/Edit')),
      exact: true,
      path: '/banks/normativity/taxes/:taxId',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Normativity/Transactions/List')),
      exact: true,
      path: '/banks/normativity/transactions',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/List')),
      exact: true,
      path: '/banks/operations',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/Edit')),
      exact: true,
      path: '/banks/operations/add',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/BulkLoad')),
      exact: true,
      path: '/banks/operations/bulk_load',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/Cancelations')),
      exact: true,
      path: '/banks/operations/cancelations',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/Conciliation')),
      exact: true,
      path: '/banks/operations/conciliation',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/Deposits')),
      exact: true,
      path: '/banks/operations/deposits',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/Requests/Add')),
      exact: true,
      path: '/banks/operations/requests/add',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/Requests/Authorize')),
      exact: true,
      path: '/banks/operations/requests/authorize',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/Reversal')),
      exact: true,
      path: '/banks/operations/reversal',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/Transfers')),
      exact: true,
      path: '/banks/operations/transfers',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/Withdrawals')),
      exact: true,
      path: '/banks/operations/withdrawals',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Banks/Operations/Edit')),
      exact: true,
      path: '/banks/operations/:operationId',
      permission: 'banks.select.listBanks',
    },
    {
      component: lazy(() => import('views/Billing/List')),
      exact: true,
      path: '/billing',
    },
    {
      component: lazy(() => import('views/Dispersion/Massive')),
      exact: true,
      path: '/dispersion/massive',
      permission: 'dispersions.select.listDispersions',
    },
    // TODO: Habilitar cuando dispersión manual esté lista
    // {
    //   component: lazy(() => import('views/Dispersion/Manual')),
    //   exact: true,
    //   path: '/dispersion/manual',
    //   //permission: 'dispersion.select.listDispersion',
    // },
    {
      component: lazy(() => import('views/Condusef/Redeco/Complaints/List')),
      exact: true,
      path: '/condusef/redeco',
      permission: 'condusef.select.listCondusef',
    },
    {
      component: lazy(() => import('views/Condusef/Redeco/Complaints/Edit')),
      exact: true,
      path: '/condusef/redeco/add',
      permission: 'condusef.insert.addCondusef',
    },
    {
      component: lazy(() => import('views/Condusef/Redeco/Complaints/Edit')),
      exact: true,
      path: '/condusef/redeco/:id',
      permission: 'condusef.update.editCondusef',
    },
    {
      component: lazy(() => import('views/Condusef/List')),
      exact: true,
      path: '/condusef/reune',
      permission: 'condusef.select.listCondusef',
    },
    {
      component: lazy(() => import('views/Condusef/Edit')),
      exact: true,
      path: '/condusef/reune/add',
      permission: 'condusef.insert.addCondusef',
    },
    {
      component: lazy(() => import('views/Condusef/Edit')),
      exact: true,
      path: '/condusef/reune/:id',
      permission: 'condusef.update.editCondusef',
    },
    {
      component: lazy(() => import('views/VulnerableActivities/LegalFramework')),
      exact: true,
      path: '/vulnerable_activities/legal_framework',
      permission: 'vulnerableActivities.select.listVulnerableActivities',
    },
    {
      component: lazy(() => import('views/VulnerableActivities/IdentificationNoticeThresholds')),
      exact: true,
      path: '/vulnerable_activities/identification_notice_thresholds',
      permission: 'vulnerableActivities.select.listVulnerableActivities',
    },
    {
      component: lazy(() => import('views/VulnerableActivities/ThresholdControl')),
      exact: true,
      path: '/vulnerable_activities/threshold_control',
      permission: 'vulnerableActivities.select.listVulnerableActivities',
    },
    {
      component: lazy(() => import('views/VulnerableActivities/ThresholdControlDetail')),
      exact: true,
      path: '/vulnerable_activities/threshold_control/:recordId',
      permission: 'vulnerableActivities.select.listVulnerableActivities',
    },
    {
      component: lazy(() => import('views/Dashboard')),
      exact: true,
      path: '/dashboard',
      permission: 'dashboard.select.listDashboard',
    },
    {
      component: lazy(() => import('views/Devices/List')),
      exact: true,
      path: '/devices',
      permission: 'devices.select.listDevices',
    },
    {
      component: lazy(() => import('views/Devices/Details')),
      exact: true,
      path: '/devices/:imei',
      permission: 'devices.select.getDevice',
    },
    {
      component: lazy(() => import('views/Logs/Binnacle/List')),
      exact: true,
      path: '/logs/binnacle',
      permission: 'logs.select.listLogs',
    },
    {
      component: lazy(() => import('views/Logs/Binnacle/List')),
      exact: true,
      path: '/logs/binnacle/:table/:id',
      permission: 'logs.select.listLogs',
    },
    {
      component: lazy(() => import('views/Notifications/List')),
      exact: true,
      path: '/notifications',
    },
    {
      component: lazy(() => import('views/Notifications/Details')),
      exact: true,
      path: '/notifications/:id',
    },
    {
      component: lazy(() => import('views/Payments/List')),
      exact: true,
      path: '/payments',
      permission: 'payments.select.listPayments',
    },
    {
      component: lazy(() => import('views/DebtCollection/Orders')),
      exact: true,
      path: '/debt_collections/orders',
      permission: 'debtCollections.select.listDebtCollections',
    },
    {
      component: lazy(() => import('views/DebtCollection/Products.V2')),
      exact: true,
      path: '/debt_collections/orders/people.v2/:peopleId/accounts/:productId',
      permission: 'debtCollections.select.listDebtCollections',
    },
    {
      component: lazy(() => import('views/DebtCollection/Managers')),
      exact: true,
      path: '/debt_collections/managers',
      permission: 'debtCollections.select.listDebtCollections',
    },
    {
      component: lazy(() => import('views/DebtCollection/Wallet')),
      exact: true,
      path: '/debt_collections/wallet',
      permission: 'debtCollections.select.listDebtCollections',
    },
    {
      component: lazy(() => import('views/DebtCollection/Products.V2')),
      exact: true,
      path: '/debt_collections/wallet/people.v2/:peopleId/accounts/:productId',
      permission: 'debtCollections.select.listDebtCollections',
    },
    {
      component: lazy(() => import('views/PaymentOrders/List')),
      exact: true,
      path: '/payment_orders',
      permission: 'paymentOrders.select.listPaymentOrders',
    },
    {
      component: lazy(() => import('views/DepositDetails/List')),
      exact: true,
      path: '/deposit_details',
      // permission: 'deposits.select.listDepositDetails',
    },
    {
      component: lazy(() => import('views/Deposits/List')),
      exact: true,
      path: '/deposits',
      // permission: 'deposits.select.listDeposits',
    },
    // FIXME: Delete this
    {
      component: lazy(() => import('views/PaymentOrders/List')),
      exact: true,
      path: '/payments_order',
      permission: 'paymentOrders.select.listPaymentOrders',
    },
    {
      component: lazy(() => import('views/Persons/List')),
      exact: true,
      path: '/people',
      permission: 'clients.select.listClients',
    },
    {
      component: lazy(() => import('views/Persons/Accounts')),
      exact: true,
      path: '/people/accounts',
    },
    {
      component: lazy(() => import('views/Persons/Add')),
      exact: true,
      path: '/people/add',
      permission: 'clients.insert.addClients',
    },
    {
      component: lazy(() => import('views/Persons/Balance')),
      exact: true,
      path: '/people/balance',
    },
    {
      component: lazy(() => import('views/Persons/Stages')),
      exact: true,
      path: '/people/stages',
    },
    {
      layout: NoteLayout,
      path: '/people/:id',
      routes: [
        {
          component: lazy(() => import('views/Persons/Edit')),
          exact: true,
          path: '/people/:peopleId',
          permission: 'clients.select.getClients',
        },
        {
          component: lazy(() => import('views/Persons/Pld')),
          exact: true,
          path: '/people/:peopleId/pld',
        },
        {
          component: lazy(() => import('views/Persons/PldModule')),
          exact: true,
          path: '/people/:peopleId/pld/:queryId',
        },
        {
          component: lazy(() => import('views/Persons/Products/Movements/List')),
          exact: true,
          path: '/people/:peopleId/products/movements',
        },
        {
          component: lazy(() => import('views/Persons/Products/Movements/Add')),
          exact: true,
          path: '/people/:peopleId/products/movements/add',
        },
        {
          component: lazy(() => import('views/Persons/Products')),
          exact: true,
          path: '/people/:peopleId/products/:productId',
        },
        {
          component: lazy(() => import('views/Persons/Redirect')),
          exact: true,
          path: '/people/:peopleId/:tabName',
        },
      ],
    },
    ///////////////////////////////////////////////////////////////////////////
    {
      layout: NoteLayout,
      component: lazy(() => import('views/Persons/Edit.V2')),
      exact: true,
      path: '/people.v2/:peopleId',
    },
    {
      component: lazy(() => import('views/Persons/Products.V2')),
      exact: true,
      path: '/people.v2/:peopleId/products/:productId',
    },
    ///////////////////////////////////////////////////////////////////////////
    {
      component: lazy(() => import('views/Aml/Alerts/List')),
      exact: true,
      path: '/pld/alerts',
      permission: 'pld.select.listAlerts',
    },
    {
      component: lazy(() => import('views/Aml/Alerts/Edit')),
      exact: true,
      path: '/pld/alerts/:alertId',
      permission: 'pld.select.listAlerts',
    },    
    {
      component: lazy(() => import('views/Aml/Reports/Calendar')),
      exact: true,
      path: '/pld/reports/calendar',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Reports/Internal/List')),
      exact: true,
      path: '/pld/reports/internal',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Reports/Internal/Edit')),
      exact: true,
      path: '/pld/reports/internal/add',
    },
    {
      component: lazy(() => import('views/Aml/Reports/Internal/Edit')),
      exact: true,
      path: '/pld/reports/internal/:reportId',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Reports/ResearchCases')),
      exact: true,
      path: '/pld/reports/investigation_cases',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Analysis/ClientAccounts')),
      exact: true,
      path: '/pld/analysis/client_movements',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Matrix/List')),
      exact: true,
      path: '/pld/analysis/risk_matrix',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Binnacle/List')),
      exact: true,
      path: '/pld/analysis/binnacle',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Matrix/Edit')),
      exact: true,
      path: '/pld/analysis/risk_matrix/add',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Matrix/Edit')),
      exact: true,
      path: '/pld/analysis/risk_matrix/:matrixId',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Matrix/Historical/List')),
      exact: true,
      path: '/pld/analysis/risk_matrix/:matrixId/historical',
    },
    {
      component: lazy(() => import('views/Aml/Matrix/Historical/Details')),
      exact: true,
      path: '/pld/analysis/risk_matrix/:matrixId/historical/:historicalId',
    },
    {
      component: lazy(() => import('views/Aml/Alerts/GroupingOfAccountsByCustomers')),
      exact: true,
      path: '/pld/customer_accounts',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Alerts/GeneralQueries')),
      exact: true,
      path: '/pld/general_queries',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Alerts/Queries')),
      exact: true,
      path: '/pld/queries',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Alerts/GroupingOfAccountsByCustomers/DetailsOfMovementsByAccount')),
      exact: true,
      path: '/pld/customer_accounts/:groupingId',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Alerts/GroupingOfAccountsByCustomers/AdditionalInformation')),
      exact: true,
      path: '/pld/customer_accounts/:groupingId/:detailId',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/AlertTypes/List')),
      exact: true,
      path: '/pld/catalogs/alert_types',
      permission: 'pld.select.listAlertTypes',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/AlertTypes/Edit')),
      exact: true,
      path: '/pld/catalogs/alert_types/add',
      permission: 'pld.insert.addAlertTypes',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/AlertTypes/Edit')),
      exact: true,
      path: '/pld/catalogs/alert_types/:id',
      permission: 'pld.update.editAlertTypes',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/Instruments')),
      exact: true,
      path: '/pld/catalogs/monetary_instruments',
      permission: 'pld.select.listInstruments',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/OppEliminar')),
      exact: true,
      path: '/pld/catalogs/opp',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/OperationTypes')),
      exact: true,
      path: '/pld/catalogs/operation_types',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/PeopleType')),
      exact: true,
      path: '/pld/catalogs/person_type',
      permission: 'pld.select.listPersonType',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/OperationMaster')),
      exact: true,
      path: '/pld/catalogs/operation_master',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/GeneralParams')),
      exact: true,
      path: '/pld/catalogs/parameters',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/Accions/List')),
      exact: true,
      path: '/pld/catalogs/actions',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/ReportingReasons')),
      exact: true,
      path: '/pld/catalogs/reporting_reasons',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/Jurisdictions')),
      exact: true,
      path: '/pld/catalogs/country_jurisdiction',
      permission: 'pld.select.listJurisdictions',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/Nationalities/list')),
      exact: true,
      path: '/pld/catalogs/nationalities',
      permission: 'pld.select.listNationalities',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/EconomicActivity/list')),
      exact: true,
      path: '/pld/catalogs/economic_activity',
      permission: 'pld.select.listEconomicActivity',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/CnbvCatalogs/list')),
      exact: true,
      path: '/pld/catalogs_cnbv',
      permission: 'pld.update.editAlertAnalysis',
    },
    {
      component: lazy(() => import('views/Aml/Catalogs/Risk')),
      exact: true,
      path: '/pld/catalogs/risk',
      permission: 'pld.select.listRisk',
    },
    {
      component: lazy(() => import('views/Aml/Analysis/BlackList/List')),
      exact: true,
      path: '/pld/analysis/black_list',
      permission: 'pld.select',
    },
    {
      component: lazy(() => import('views/Aml/Analysis/BlackList/List/OpenSearchTab/Edit')),
      exact: true,
      path: '/pld/analysis/black_list/add',
    },
    {
      component: lazy(() => import('views/Aml/Analysis/BlackList/List/OpenSearchTab/Edit')),
      exact: true,
      path: '/pld/analysis/black_list/:id',
    },
    {
      component: lazy(() => import('views/Aml/OpenSearch/List')),
      exact: true,
      path: '/pld/catalogs/black_list',
      permission: 'pld.select.listBlackList',
    },
    {
      component: lazy(() => import('views/Aml/OpenSearch/Edit')),
      exact: true,
      path: '/pld/catalogs/black_list/add',
      permission: 'pld.insert.addPeopleToBlackList',
    },
    {
      component: lazy(() => import('views/Aml/OpenSearch/Edit')),
      exact: true,
      path: '/pld/catalogs/black_list/:id',
      permission: 'pld.update.editBlackList',
    },
    {
      component: lazy(() => import('views/Reports/List')),
      exact: true,
      path: '/reports',
      permission: 'reports.select.listReports',
    },
    {
      component: lazy(() => import('views/Reports/Edit')),
      exact: true,
      path: '/reports/add',
    },
    {
      component: lazy(() => import('views/Reports/Edit')),
      exact: true,
      path: '/reports/:reportId',
    },
    {
      component: lazy(() => import('views/Reports/System')),
      exact: true,
      path: '/reports/system/:name',
    },
    {
      component: lazy(() => import('views/Settings/Cognito/List')),
      exact: true,
      path: '/settings/access/cognito',
      permission: 'poolId.select.listpoolId',
    },
    {
      component: lazy(() => import('views/Settings/Access/Groups')),
      exact: true,
      path: '/settings/access/groups',
      permission: 'groups.select.listGroups',
    },
    {
      component: lazy(() => import('views/Settings/Access/Managers/List')),
      exact: true,
      path: '/settings/access/managers',
    },
    {
      component: lazy(() => import('views/Settings/Access/Roles/List')),
      exact: true,
      path: '/settings/access/roles',
      permission: 'roles.select.listRoles',
    },
    {
      component: lazy(() => import('views/Settings/Access/Roles/Edit')),
      exact: true,
      path: '/settings/access/roles/add',
      permission: 'roles.insert.addRoles',
    },
    {
      component: lazy(() => import('views/Settings/Access/Roles/Edit')),
      exact: true,
      path: '/settings/access/roles/:rolId',
      permission: 'roles.update.updateRoles',
    },
    {
      component: lazy(() => import('views/Settings/Access/Users/List')),
      exact: true,
      path: '/settings/access/users',
      permission: 'users.select.listUsers',
    },
    {
      component: lazy(() => import('views/Settings/Access/Users/Details')),
      exact: true,
      path: '/settings/access/users/:userId',
    },
    {
      component: lazy(() => import('views/Settings/Assistant')),
      exact: true,
      path: '/settings/assistant',
      permission: 'assistant.select.listAssistant',
    },
    {
      component: lazy(() => import('views/Settings/Assistant.new')),
      exact: true,
      path: '/settings/assistant_new',
      permission: 'assistant.select.listAssistant',
    },
    {
      component: lazy(() => import('views/Settings/Calendar')),
      exact: true,
      path: '/settings/calendar',
      permission: 'calendar.select.listCalendar',
    },
    {
      component: lazy(() => import('views/Settings/Campaigns/List')),
      exact: true,
      path: '/settings/campaigns',
      permission: 'campaigns.select.listCampaigns',
    },
    {
      component: lazy(() => import('views/Settings/Campaigns/Edit')),
      exact: true,
      path: '/settings/campaigns/add',
      // permission: 'campaigns.insert.addCampaigns',
    },
    {
      component: lazy(() => import('views/Settings/Campaigns/Edit')),
      exact: true,
      path: '/settings/campaigns/:campaignId',
      permission: 'campaigns.update.updateCampaigns',
    },
    {
      component: lazy(() => import('views/Settings/Campaigns/Historical')),
      exact: true,
      path: '/settings/campaigns/:campaignId/historical',
      permission: 'campaigns.select.getCampaigns',
    },
    {
      component: lazy(() => import('views/Settings/Campaigns/Historical/Details')),
      exact: true,
      path: '/settings/campaigns/:campaignId/historical/:historicalId',
      permission: 'campaigns.select.getCampaigns',
    },
    {
      component: lazy(() => import('views/Settings/Marketplace/List')),
      exact: true,
      path: '/settings/marketplace',
      // permission: 'marketplace.select.listMarketplace',
    },
    {
      component: lazy(() => import('views/Settings/Marketplace/Details')),
      exact: true,
      path: '/settings/marketplace/:id',
      // permission: 'marketplace.update.editMarketplace',
    },
    {
      component: lazy(() => import('views/Settings/Company')),
      exact: true,
      path: '/settings/company',
      permission: 'company.select.getCompany',
    },
    {
      component: lazy(() => import('views/Settings/Events/List')),
      exact: true,
      path: '/settings/events',
      permission: 'events.select.listEvents',
    },
    {
      component: lazy(() => import('views/Settings/Events/Edit')),
      exact: true,
      path: '/settings/events/add',
      permission: 'events.insert.addEvent',
    },
    {
      component: lazy(() => import('views/Settings/Events/Edit')),
      exact: true,
      path: '/settings/events/:idEvent',
      permission: 'events.update.edittEvent',
    },
    {
      component: lazy(() => import('views/Settings/Flows/List')),
      exact: true,
      path: '/settings/flows',
      permission: 'flows.select.listFlows',
    },
    {
      component: lazy(() => import('views/Settings/Flows/Edit')),
      exact: true,
      path: '/settings/flows/add',
      permission: 'flows.insert.addFlows',
    },
    {
      component: lazy(() => import('views/Settings/Flows/Edit')),
      exact: true,
      path: '/settings/flows/:flowId',
      permission: 'flows.select.getFlows',
    },
    {
      component: lazy(() => import('views/Settings/Parameters/List')),
      exact: true,
      path: '/settings/parameters',
      permission: 'parameters.select.listParameters',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Forms/List')),
      exact: true,
      path: '/settings/templates/forms',
      permission: 'forms.select.listForms',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Forms/Edit')),
      exact: true,
      path: '/settings/templates/forms/add',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Forms/Edit')),
      exact: true,
      path: '/settings/templates/forms/:formId',
    },
    {
      component: lazy(() => import('views/Settings/Templates/People/List')),
      exact: true,
      path: '/settings/templates/people',
      permission: 'clientTypes.select.listClientTypes',
    },
    {
      component: lazy(() => import('views/Settings/Templates/People/List/SuggestedFormView')),
      exact: true,
      path: '/settings/templates/people/add/suggested',
    },
    {
      component: lazy(() => import('views/Settings/Templates/People/Edit')),
      exact: true,
      path: '/settings/templates/people/add',
    },
    {
      component: lazy(() => import('views/Settings/Templates/People/Relationships/List')),
      exact: true,
      path: '/settings/templates/people/relationships',
    },
    {
      component: lazy(() => import('views/Settings/Templates/People/Stages/List')),
      exact: true,
      path: '/settings/templates/people/stages',
    },
    {
      component: lazy(() => import('views/Settings/Templates/People/Stages/Edit')),
      exact: true,
      path: '/settings/templates/people/stages/add',
    },
    {
      component: lazy(() => import('views/Settings/Templates/People/Stages/Edit')),
      exact: true,
      path: '/settings/templates/people/stages/:id',
    },
    {
      component: lazy(() => import('views/Settings/Templates/People/Edit')),
      exact: true,
      path: '/settings/templates/people/:peopleTypeId',
      permission: 'clientTypes.update.updateClientTypes',
    },
    {
      component: lazy(() => import('views/Settings/Templates/People/Evaluations/List')),
      exact: true,
      path: '/settings/templates/people/:peopleTypeId/evaluations',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Products/List')),
      exact: true,
      path: '/settings/templates/products',
      permission: 'productTypes.select.listProductTypes',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Products/Edit')),
      exact: true,
      path: '/settings/templates/products/add',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Products/Edit')),
      exact: true,
      path: '/settings/templates/products/:productId',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Products/Simulator')),
      exact: true,
      path: '/settings/templates/products/:productId/simulator',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Notifications/List')),
      exact: true,
      path: '/settings/templates/notifications',
      permission: 'notificationTypes.select.listNotificationTypes',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Notifications/Edit')),
      exact: true,
      path: '/settings/templates/notifications/add',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Notifications/Edit')),
      exact: true,
      path: '/settings/templates/notifications/:templateId',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Notifications/Edit.tiny')),
      exact: true,
      path: '/settings/templates/notifications.v2/add',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Notifications/Edit.tiny')),
      exact: true,
      path: '/settings/templates/notifications.v2/:id',
    },
    {
      component: lazy(() => import('views/Settings/Templates/NotificationsConfiguration/List')),
      exact: true,
      path: '/settings/templates/notificationsType',
      permission: 'notificationChannels.select.listNotificationChannels',
    },
    {
      component: lazy(() => import('views/Settings/Templates/NotificationsConfiguration/Edit')),
      exact: true,
      path: '/settings/templates/notificationsType/add',
      permission: 'notificationChannels.insert.addNotificationChannels',
    },
    {
      component: lazy(() => import('views/Settings/Templates/NotificationsConfiguration/Edit')),
      exact: true,
      path: '/settings/templates/notificationsType/:id',
      permission: 'notificationChannels.update.updateNotificationChannels',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Documents/List')),
      exact: true,
      path: '/settings/templates/documents',
      permission: 'documentsTypes.select.listDocumentsTypes',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Documents/Edit')),
      exact: true,
      path: '/settings/templates/documents/add',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Documents/DocumentKit/List')),
      exact: true,
      path: '/settings/templates/documents/kit',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Documents/DocumentKit/Add')),
      exact: true,
      path: '/settings/templates/documents/kit/add',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Documents/DocumentKit/Add')),
      exact: true,
      path: '/settings/templates/documents/kit/:kitId',
    },
    {
      component: lazy(() => import('views/Settings/Templates/Documents/Edit')),
      exact: true,
      path: '/settings/templates/documents/:templateId',
    },
    {
      component: lazy(() => import('views/Cards/List')),
      exact: true,
      path: '/cards',
      permission: 'cards.select.listCards',
    },
    {
      component: lazy(() => import('views/Cards/Details')),
      exact: true,
      path: '/cards/:id/:account/:client',
      permission: 'cards.select.listCards',
    },
    {
      component: lazy(() => import('views/CardsRequests/List')),
      exact: true,
      path: '/support',
      permission: 'cardSupport.select.listCardSupport',
    },
    {
      layout: NoteLayout,
      component: lazy(() => import('views/CardsRequests/Details')),
      exact: true,
      path: '/support/:id',
      permission: 'cardSupport.select.listCardSupport',
    },
    {
      component: lazy(() => import('views/PhysicalCards/List')),
      exact: true,
      path: '/physical_cards',
      permission: 'shipmentTracking.select.listShipmentTracking',
    },
    {
      component: lazy(() => import('views/PhysicalCards/Edit')),
      exact: true,
      path: '/physical_cards/add',
      permission: 'shipmentTracking.insert.addShipmentTracking',
    },
    {
      component: lazy(() => import('views/PhysicalCards/Edit')),
      exact: true,
      path: '/physical_cards/:id',
      permission: 'shipmentTracking.update.editShipmentTracking',
    },
    {
      component: lazy(() => import('views/PhysicalCards/Details')),
      exact: true,
      path: '/physical_cards/details/:id',
      permission: 'shipmentTracking.select.listShipmentTracking',
    },
    {
      component: lazy(() => import('views/Invoice/List')),
      exact: true,
      path: '/invoice',
      permission: 'invoice.select.listInvoice',
    },
    {
      component: lazy(() => import('views/Invoice/Mutate')),
      exact: true,
      path: '/invoice/add',
      permission: 'invoice.insert.addInvoice',
    },
    {
      component: lazy(() => import('views/Invoice/Mutate')),
      exact: true,
      path: '/invoice/edit/:invoiceId',
      permission: 'invoice.insert.addInvoice',
    },
    {
      component: lazy(() => import('views/Invoice/Details')),
      exact: true,
      path: '/invoice/:idInvoice',
      permission: 'invoice.select.getBilling',
    },
    {
      component: lazy(() => import('views/Transactions/List')),
      exact: true,
      path: '/movements/transactions',
    },
    {
      component: lazy(() => import('views/Facturation/List')),
      exact: true,
      path: '/finance/billing',
      permission: 'billing.select.listBilling',
    },
    {
      component: lazy(() => import('views/Commerce/List')),
      exact: true,
      path: '/commerce',
      permission: 'commerce.select.listCommerce',
    },
    {
      component: lazy(() => import('views/Commerce/Add')),
      exact: true,
      path: '/commerce/add',
      permission: 'commerce.select.addCommerce',
    },
    {
      component: lazy(() => import('views/Developers/List')),
      exact: true,
      path: '/developers',
      permission: 'developers.select.listDevelopers',
    },
    {
      component: lazy(() => import('views/PaymentLinks/List')),
      exact: true,
      path: '/payment_links',
      permission: 'payments.select.listLinks',
    },
    {
      component: lazy(() => import('views/PaymentLinks/Edit')),
      exact: true,
      path: '/payment_links/add',
      permission: 'payments.select.listLinks',
    },
    {
      component: lazy(() => import('views/Settings/CustomLists/Home')),
      exact: true,
      path: '/settings/custom_lists',
      permission: 'genericList.select.listGenericList',
    },
    {
      component: lazy(() => import('views/Settings/CustomLists/ListDir')),
      exact: true,
      path: '/settings/custom_lists/List',
      permission: 'genericList.select.listGenericList',
    },
    {
      component: lazy(() => import('views/Settings/CustomLists/Add')),
      exact: true,
      path: '/settings/custom_lists/add',
      permission: 'genericList.insert.addGenericList',
    },
    {
      component: lazy(() => import('views/Settings/CustomLists/Dispatcher')),
      exact: true,
      path: '/settings/custom_lists/List/:id',
      permission: 'genericList.select.listGenericList',
    },
    {
      component: lazy(() => import('views/AccountingAccounts/AccountsList')),
      exact: true,
      path: '/accounting/accounts',
      permission: 'accounting.select.listChartOfAccounts',
    },
    {
      component: lazy(() => import('views/AccountingAccounts/Journal')),
      exact: true,
      path: '/accounting/journal',
      permission: 'accounting.select.listJournalEntry',
    },
    {
      component: lazy(() => import('views/AccountingAccounts/Poliza/List')),
      exact: true,
      path: '/accounting/poliza',
      permission: 'accounting.select.listGeneralJournal',
    },
    {
      component: lazy(() => import('views/AccountingAccounts/Poliza/Add')),
      exact: true,
      path: '/accounting/poliza/add',
      permission: 'accounting.insert.addAccounting',
    },
    {
      component: lazy(() => import('views/AccountingAccounts/Poliza/Detail')),
      exact: true,
      path: '/accounting/poliza/detail/:id',
      permission: 'accounting.select.listGeneralJournal',
    },
    {
      component: lazy(() => import('views/AccountingAccounts/BankReconciliation/index')),
      exact: true,
      path: '/accounting/bank_reconciliation',
      permission: 'accounting.select.listBankReconciliation',
    },
    error404,
  ],
};

export default dashboardRoutes;
